import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  IonContent,
  IonPage,
  IonFooter,
  IonItem,
  IonAccordion,
  IonAccordionGroup,
  IonCheckbox,
  useIonAlert,
  useIonToast,
} from '@ionic/react'
import { CustomSpacer, DetailContent, FooterButtons, StyleIonHeader, StyleIonInputNew } from '../../../components'
import { TextStyle } from '../../../components/atoms/text'
import { ProductInfo } from '../components'
import { DivideLineBold, DivideLine } from '../../../components/atoms/divide'
import { RouteComponentProps } from 'react-router'
import { observer } from 'mobx-react'
import moment from 'moment-timezone'
import { StoresContext } from '../../../provider/StoreProvider'
import { checkbox, warning } from 'ionicons/icons'
import { VALIDATE_STATUS, amountString } from '../../../common'

type Props = RouteComponentProps<{
  voteId: string
}>

export const ProductDisposal: React.FC<Props> = observer(({ match }) => {
  const [presentAlert] = useIonAlert()
  const [presentToast] = useIonToast()
  const { myStore } = useContext(StoresContext)
  const [validateStatus, setValidateStatus] = useState<VALIDATE_STATUS>(VALIDATE_STATUS.INPUT)
  let loadParam = { voteId: Number(match.params.voteId) }
  const detailStore = myStore.productDisposalStore

  useEffect(() => {
    loadParam = { voteId: Number(match.params.voteId) }
    detailStore.load({ param: loadParam }, true)
  }, [match.params.voteId])

  const [inputValues, setInputValues] = useState({
    rrn: '',
  })
  const { rrn } = inputValues

  const onChangeInput = (event: { target: { value: string | number; name: string | number } }) => {
    const { value, name } = event.target
    setInputValues({ ...inputValues, [name]: value })
  }

  const [checkedTerms, setCheckedTerms] = useState({ terms1: false, terms2: false })

  const onCheckChange = (event: { target: { name: string; checked: boolean } }) => {
    const name = event.target.name
    setCheckedTerms({
      ...checkedTerms,
      [name]: event.target.checked,
    })
  }

  const validate = () => {
    if (rrn?.length > 0 && checkedTerms.terms1 === true && checkedTerms.terms2 === true) {
      setValidateStatus(VALIDATE_STATUS.OKAY)
    } else {
      setValidateStatus(VALIDATE_STATUS.INPUT)
    }
  }

  useEffect(() => {
    validate()
  }, [rrn, checkedTerms])

  const onDisposalVote = async (voteType: 'ACCEPT' | 'REJECT') => {
    presentAlert({
      mode: 'md',
      header: `매각을 ${voteType === 'ACCEPT' ? '찬성' : '반대'}하시겠습니까?`,
      cssClass: 'custom-alert-alert ',
      buttons: [
        {
          text: '취소',
          cssClass: 'alert-button-cancel',
        },
        {
          text: `${voteType === 'ACCEPT' ? '찬성' : '반대'}`,
          cssClass: 'alert-button-confirm',
          handler: async () => {
            const voteResult = await myStore.disposalVote(Number(detailStore.content?.id), voteType)
            if (voteResult) {
              presentToast({
                message: `작품에 대한 매각을 ${voteType === 'ACCEPT' ? '찬성' : '반대'}했습니다.`,
                duration: 3000,
                position: 'top',
                color: 'primary',
                icon: checkbox,
              })
              setTimeout(() => {
                location.reload()
              }, 300)
            } else {
              presentToast({
                message: `투표에 실패했습니다. 잠시 후 다시 시도해주세요!`,
                duration: 3000,
                position: 'top',
                color: 'danger',
                icon: warning,
              })
            }
          },
        },
      ],
    })
    detailStore.load({ param: loadParam }, true)
  }

  const onSettlement = async () => {
    const settlementResult = await myStore.settlement(Number(detailStore.content?.id), { rrn: rrn })
    if (settlementResult) {
      presentToast({
        message: `정산 신청이 완료되었습니다.`,
        duration: 3000,
        position: 'top',
        color: 'primary',
        icon: checkbox,
      })
      setTimeout(() => {
        location.reload()
      }, 300)
    } else {
      presentToast({
        message: `정산 요청에 실패했습니다. 잠시 후 다시 시도해주세요!`,
        duration: 3000,
        position: 'top',
        color: 'danger',
        icon: warning,
      })
    }
  }

  return (
    <IonPage>
      <StyleIonHeader type={'SUB'} title={'매각 관련'} href={`/myPage/asset/${detailStore.content?.product.id}`} />
      <IonContent>
        <DetailContent detailStore={detailStore}>
          <ProductInfo
            artist={detailStore.content?.product.artist || '-'}
            title={detailStore.content?.product.name || '-'}
            img={detailStore.content?.product.imageUri || '-'}
            id={detailStore.content?.product.id || 0}
          />
          <DivideLineBold />
          {/* 투표 진행 중 화면 */}
          {detailStore.content?.status === 'VOTING' && (
            <>
              <SectionContainer>
                <BetweenWrap>
                  <TextStyle type={'r_14'}>투표 기간</TextStyle>
                  <TextStyle type={'r_14'}>
                    {`${moment(new Date(detailStore.content?.voteDate)).format('YYYY.MM.DD')} ~ ${moment(
                      new Date(detailStore.content?.untilDate),
                    ).format('YYYY.MM.DD')}` ?? '-'}
                  </TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>투표율</TextStyle>
                  <TextStyle type={'r_14'}>{amountString(String(myStore.voteRateDisposal), false, 2) ?? 0}%</TextStyle>
                </BetweenWrap>
              </SectionContainer>
              <DivideLine isShort={true} />
              <SectionContainer>
                <TextStyle type={'b_16'}>작품 매각</TextStyle>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>예상 매각 금액</TextStyle>
                  <TextStyle type={'r_14'}>{detailStore.content?.salePrice.toLocaleString() ?? 0} 원</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>예상 조각 단가</TextStyle>
                  <TextStyle type={'r_14'}>{detailStore.content?.revenue.sellCost.toLocaleString() ?? 0} 원</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <DivideLine />
                <CustomSpacer spacing="16px" />
                <TextStyle type={'b_16'}>보유중인 작품 정보</TextStyle>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>조각 평단가</TextStyle>
                  <TextStyle type={'r_14'}>
                    {detailStore.content?.revenue.averageCost.toLocaleString() ?? 0} 원
                  </TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>수량</TextStyle>
                  <TextStyle type={'r_14'}>{detailStore.content?.revenue.count.toLocaleString() ?? 0} 개</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>예상 수익률</TextStyle>
                  <TextStyle
                    type={'b_16'}
                    color={
                      (myStore.expectedCostRate || 0) > 0
                        ? 'error_main'
                        : (myStore.expectedCostRate || 0) < 0
                        ? 'secondary_main'
                        : 'primary_dark'
                    }
                  >
                    {`${(myStore.expectedCostRate || 0) > 0 ? '+' : ''}${amountString(
                      String(myStore.expectedCostRate),
                      false,
                      2,
                    )}%`}
                  </TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>
                    예상 정산 금액<br></br>(세금 미포함)
                  </TextStyle>
                  <TextStyle type={'b_16'}>{Math.floor(myStore.expectedCost || 0).toLocaleString()} 원</TextStyle>
                </BetweenWrap>
              </SectionContainer>
              <CustomSpacer spacing="40px" />
              <BottomContainer>
                <TextStyle type={'m_11'} color={'primary_light'}>
                  {detailStore.content?.voteType
                    ? '이미 투표를 완료했습니다'
                    : '매각 투표를 완료한 이후에는 선택을 수정할 수 없습니다.'}
                </TextStyle>
              </BottomContainer>
            </>
          )}

          {/* 투표 종료 후 화면 */}
          {detailStore.content?.status === 'FINISH' && detailStore.content?.userProductStatus === 'PURCHASED' && (
            <>
              <SectionContainer>
                <BetweenWrap>
                  <TextStyle type={'b_16'}>투표 결과</TextStyle>
                  {detailStore.content?.isSellApproved === true && (
                    <TextStyle type={'b_16'} color={'secondary_main'}>
                      찬성 {amountString(String(myStore.approveVoteRate), false, 2)}%
                    </TextStyle>
                  )}
                  {detailStore.content?.isSellApproved === false && (
                    <TextStyle type={'b_16'} color={'error_main'}>
                      반대 {amountString(String(myStore.rejectVoteRate), false, 2)}%
                    </TextStyle>
                  )}
                  {detailStore.content?.isSellApproved == null && (
                    <TextStyle type={'b_16'} color={'primary_main'}>
                      집계중
                    </TextStyle>
                  )}
                </BetweenWrap>
                <CardVoteContainer
                  style={{
                    backgroundColor:
                      detailStore.content?.isSellApproved === true
                        ? '#ebf3fe'
                        : detailStore.content?.isSellApproved === false
                        ? '#FDEDEC'
                        : '#cccccc',
                  }}
                >
                  <CardContentContainer>
                    <TextStyle type={'r_13'} color={'error_main'}>
                      반대
                    </TextStyle>
                    <TextStyle
                      type={'b_15'}
                      style={{ color: '#000000' }}
                    >{`${detailStore.content?.rejectCount.toLocaleString()}`}</TextStyle>
                  </CardContentContainer>
                  <div
                    className="vertical-line"
                    style={{
                      borderColor:
                        detailStore.content?.isSellApproved === true
                          ? '#CAE0FF'
                          : detailStore.content?.isSellApproved === false
                          ? '#FFC6C2'
                          : '#7F8386',
                    }}
                  />
                  <CardContentContainer>
                    <TextStyle type={'r_13'} color={'secondary_main'}>
                      찬성
                    </TextStyle>
                    <TextStyle
                      type={'b_15'}
                      style={{ color: '#000000' }}
                    >{`${detailStore.content?.approveCount.toLocaleString()}`}</TextStyle>
                  </CardContentContainer>
                </CardVoteContainer>
                <BetweenWrap>
                  <TextStyle type={'r_14'}>조각 유통량</TextStyle>
                  <TextStyle
                    type={'r_14'}
                  >{`${detailStore.content?.suppliedTokenCount.toLocaleString()} 개`}</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>유통량 대비 참여 투표율</TextStyle>
                  <TextStyle type={'r_14'}>{`${amountString(String(myStore.voteRateDisposal), false, 2)} %`}</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>찬성 수량</TextStyle>
                  <TextStyle type={'r_14'}>{`${detailStore.content?.approveCount.toLocaleString()} 표`}</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>반대 수량</TextStyle>
                  <TextStyle type={'r_14'}>{`${detailStore.content?.rejectCount.toLocaleString()} 표`}</TextStyle>
                </BetweenWrap>
              </SectionContainer>
              <DivideLine isShort={true} />
              <SectionContainer>
                <BetweenWrap>
                  <TextStyle type={'r_14'}>총 분할 소유권 수량</TextStyle>
                  <TextStyle type={'r_14'}>
                    {detailStore.content?.suppliedTokenCount.toLocaleString() ?? 0} 개
                  </TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>투표행사 수량</TextStyle>
                  <TextStyle type={'r_14'}>{myStore.totalVoteCount.toLocaleString()} 개</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>투표기간 명시</TextStyle>
                  <TextStyle type={'r_14'}>
                    {`~ ${moment(new Date(detailStore.content?.untilDate)).format('YYYY.MM.DD HH:MM')} `}
                  </TextStyle>
                </BetweenWrap>
              </SectionContainer>
              <DivideLine isShort={true} />
              <SectionContainer>
                <BetweenWrap>
                  <TextStyle type={'r_14'}>매각 예상 금액</TextStyle>
                  <TextStyle type={'r_14'}>{detailStore.content?.salePrice.toLocaleString()} 원</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>보유 수량</TextStyle>
                  <TextStyle type={'r_14'}>{detailStore.content?.revenue.count.toLocaleString()} 개</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>예상 수익률</TextStyle>
                  <TextStyle
                    type={'b_16'}
                    color={
                      (myStore.expectedCostRate || 0) > 0
                        ? 'error_main'
                        : (myStore.expectedCostRate || 0) < 0
                        ? 'secondary_main'
                        : 'primary_dark'
                    }
                  >
                    {`${(myStore.expectedCostRate || 0) > 0 ? '+' : ''}${amountString(
                      String(myStore.expectedCostRate),
                      false,
                      2,
                    )}%`}
                  </TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <BetweenWrap>
                  <TextStyle type={'r_14'}>예상 수익금</TextStyle>
                  <TextStyle type={'b_16'}>{Math.floor(myStore.expectedCost || 0).toLocaleString()} 원</TextStyle>
                </BetweenWrap>
              </SectionContainer>
              <DivideLine isShort={true} />
              <GuideVoteContainer>
                {detailStore.content?.isSellApproved === true && (
                  <>
                    <BetweenWrap>
                      <TextStyle type={'r_14'}>진행상황</TextStyle>
                      <TextStyle type={'b_16'} color={'secondary_main'}>
                        매각 진행중
                      </TextStyle>
                    </BetweenWrap>
                    <CustomSpacer spacing="6px" />
                    <TextStyle type={'r_14'}>투표결과에 따라 매각이 진행됩니다.</TextStyle>
                    <TextStyle type={'r_14'}>매각 진행 기간은 약 2주간 소요됩니다.</TextStyle>
                  </>
                )}
                {detailStore.content?.isSellApproved === false && (
                  <>
                    <BetweenWrap>
                      <TextStyle type={'r_14'}>진행상황</TextStyle>
                      <TextStyle type={'b_16'} color={'error_main'}>
                        매각 부결
                      </TextStyle>
                    </BetweenWrap>
                    <CustomSpacer spacing="6px" />
                    <TextStyle type={'r_14'}>투표결과에 따라 매각이 진행됩니다.</TextStyle>
                    <TextStyle type={'r_14'}>해당 작품은 마켓에 재오픈됩니다.</TextStyle>
                    <TextStyle type={'r_14'}>
                      ({`${moment(new Date(detailStore.content?.tradeRestartDate)).format('YY/MM/DD HH:MM')} 예정`})
                    </TextStyle>
                  </>
                )}
                {detailStore.content?.isSellApproved == null && (
                  <>
                    <BetweenWrap>
                      <TextStyle type={'r_14'}>진행상황</TextStyle>
                      <TextStyle type={'b_16'} color={'primary_main'}>
                        투표 집계중
                      </TextStyle>
                    </BetweenWrap>
                    <CustomSpacer spacing="6px" />
                    <TextStyle type={'r_14'}>투표 결과를 최종적으로 집계하고 있습니다.</TextStyle>
                    <TextStyle type={'r_14'}>빠른 시일내에 결과를 알려드리겠습니다 .</TextStyle>
                  </>
                )}
                <CustomSpacer spacing="24px" />
              </GuideVoteContainer>
              <DivideLine isShort={true} />
              <GuideTradeContainer>
                <TextStyle type={'r_13'} color={'primary_light'}>
                  안내
                </TextStyle>
                <ul>
                  <li>안내 투표가 진행되는 동안 마켓 거래는 중단됩니다.</li>
                  <li>과반수가 넘는 매각 투표 결과(찬성/반대)에 따라 위 상품의 매각 또는 보류 여부가 결정됩니다.</li>
                  <li>매각 부결시, 상품은 마켓에 재오픈 됩니다</li>
                </ul>
              </GuideTradeContainer>
            </>
          )}

          {/* 투표 종료 후 화면 */}
          {detailStore.content?.status === 'FINISH' && detailStore.content?.userProductStatus !== 'PURCHASED' && (
            <>
              {detailStore.content?.userProductStatus === 'FINISH' && (
                <StyleBox>
                  <InfoTextContainer>
                    <TextStyle type={'b_16'}>정산 완료 내역입니다.</TextStyle>
                    <TextStyle type={'b_11'}>작품 정산시 최종 매각가에서 최초 판매가를 차감한 금액의 10%가</TextStyle>
                    <TextStyle type={'b_11'}>매각 수수료로 공제되어 반영되었습니다. </TextStyle>
                  </InfoTextContainer>
                </StyleBox>
              )}
              <CardFinishContainer>
                <CardContentContainer>
                  <TextStyle type={'r_13'} color={'success_light'}>
                    보유수량
                  </TextStyle>
                  <TextStyle
                    type={'b_15'}
                    style={{ color: '#ffffff' }}
                  >{`${detailStore.content?.revenue.count.toLocaleString()} 개`}</TextStyle>
                </CardContentContainer>
                <div className="vertical-line" />
                <CardContentContainer>
                  <TextStyle type={'r_13'} color={'success_light'}>
                    {detailStore.content?.userProductStatus !== 'FINISH' ? '정산 예정 금액' : '정산 금액'}
                  </TextStyle>
                  <TextStyle type={'b_15'} style={{ color: '#ffffff' }}>
                    {`${detailStore.content?.revenue.amount.toLocaleString()} 원`}
                  </TextStyle>
                </CardContentContainer>
              </CardFinishContainer>
              <IonAccordionGroup>
                <IonAccordion value="first">
                  <StyleIonItem slot="header"></StyleIonItem>
                  <div slot="content">
                    <AccordionContentWrap>
                      <BetweenWrap>
                        <TextStyle type={'r_14'}>판매일</TextStyle>
                        <TextStyle type={'r_14'}>{`${moment(new Date(detailStore.content?.createdDate)).format(
                          'YYYY.MM.DD',
                        )}`}</TextStyle>
                      </BetweenWrap>
                      <BetweenWrap>
                        <TextStyle type={'r_14'}>매각일</TextStyle>
                        <TextStyle type={'r_14'}>
                          {detailStore.content?.sellHistory
                            ? `${moment(new Date(detailStore.content?.sellHistory?.saleDate)).format('YYYY.MM.DD')}`
                            : `${moment(new Date(detailStore.content?.expectedSellDate)).format('YYYY.MM.DD')}`}
                        </TextStyle>
                      </BetweenWrap>
                      <BetweenWrap>
                        <TextStyle type={'r_14'}>보유일</TextStyle>
                        <TextStyle type={'r_14'}>
                          {`${Math.max(
                            0,
                            Math.floor(
                              detailStore.content?.sellHistory
                                ? (new Date(detailStore.content?.sellHistory?.saleDate).getTime() -
                                    new Date(detailStore.content?.sellHistory?.createdDate).getTime()) /
                                    (3600000 * 24)
                                : (new Date(detailStore.content?.expectedSellDate).getTime() -
                                    new Date(detailStore.content?.createdDate).getTime()) /
                                    (3600000 * 24),
                            ),
                          )} 일`}
                        </TextStyle>
                      </BetweenWrap>
                      <BetweenWrap>
                        <TextStyle type={'r_14'}>최초 작품 금액</TextStyle>
                        <TextStyle type={'r_14'}>{`${myStore.initSalePriceDisposal.toLocaleString()} 원`}</TextStyle>
                      </BetweenWrap>
                      <BetweenWrap>
                        <TextStyle type={'r_14'}>직픔 매각 금액</TextStyle>
                        <TextStyle type={'b_16'}>
                          {detailStore.content?.sellHistory
                            ? `${detailStore.content?.sellHistory?.salePrice.toLocaleString()} 원`
                            : `${detailStore.content?.salePrice.toLocaleString()} 원`}
                        </TextStyle>
                      </BetweenWrap>
                      <BetweenWrap>
                        <TextStyle type={'r_14'}>판매가격 대비 가치 상승률</TextStyle>
                        <TextStyle type={'b_15'} color={'error_main'}>
                          {detailStore.content?.sellHistory
                            ? amountString(
                                String(
                                  ((detailStore.content?.sellHistory?.salePrice - myStore.initSalePriceDisposal) /
                                    myStore.initSalePriceDisposal) *
                                    100,
                                ),
                                false,
                                2,
                              )
                            : amountString(
                                String(
                                  ((detailStore.content?.salePrice - myStore.initSalePriceDisposal) /
                                    myStore.initSalePriceDisposal) *
                                    100,
                                ),
                                false,
                                2,
                              )}
                          %
                        </TextStyle>
                      </BetweenWrap>
                    </AccordionContentWrap>
                  </div>
                </IonAccordion>
              </IonAccordionGroup>
              <DivideLineBold />
              <SectionContainer>
                <BetweenWrap>
                  <TextStyle type={'b_16'}>취득가액</TextStyle>
                  <TextStyle
                    type={'b_16'}
                  >{`${detailStore.content?.revenue.purchaseBalance.toLocaleString()} 원`}</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="16px" />
                <DivideLine />
                <ContentWrap>
                  <BetweenWrap>
                    <TextStyle type={'r_14'}>양도가액</TextStyle>
                    <TextStyle
                      type={'r_14'}
                    >{`${detailStore.content?.revenue.originRevenue.toLocaleString()} 원`}</TextStyle>
                  </BetweenWrap>
                  <BetweenWrap>
                    <TextStyle type={'r_14'}>기타 소득세</TextStyle>
                    <TextStyle
                      type={'r_14'}
                    >{`${detailStore.content?.revenue.otherIncomeTax.toLocaleString()} 원`}</TextStyle>
                  </BetweenWrap>
                  <BetweenWrap>
                    <TextStyle type={'r_14'}>지방 소득세</TextStyle>
                    <TextStyle
                      type={'r_14'}
                    >{`${detailStore.content?.revenue.localTax.toLocaleString()} 원`}</TextStyle>
                  </BetweenWrap>
                </ContentWrap>
                <DivideLine />
                <CustomSpacer spacing="16px" />
                <IonAccordionGroup>
                  <IonAccordion value={'price'}>
                    <StyleIonItemPrice slot={'header'}>
                      <BetweenWrap>
                        <TextStyle type={'b_16'}>
                          {detailStore.content?.userProductStatus !== 'FINISH' ? '정산 예정 금액' : '정산 금액'}
                        </TextStyle>
                        <TextStyle
                          type={'r_14'}
                        >{`${detailStore.content?.revenue.amount.toLocaleString()} 원`}</TextStyle>
                      </BetweenWrap>
                    </StyleIonItemPrice>
                    <ContentContainer slot="content">
                      <TextStyle type={'b_12'} color={'primary_light'}>
                        {'나의 양도소득 산출 내역'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 양도가액: ${detailStore.content?.revenue.originRevenue.toLocaleString()} 원`}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 취득가액: ${detailStore.content?.revenue.purchaseBalance.toLocaleString()} 원`}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 취득 환산가액: ${detailStore.content?.revenue.calculatedBalance.toLocaleString()} 원`}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 인정 필요경비: ${detailStore.content?.revenue.appliedExpense.toLocaleString()} 원`}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 기타 소득금액: ${detailStore.content?.revenue.otherIncome.toLocaleString()} 원`}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 과세대상 금액: 기타 소득 금액 5만원 이상일 시 해당`}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 기타 소득세: ${detailStore.content?.revenue.otherIncomeTax.toLocaleString()} 원`}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {`- 지방 소득세: ${detailStore.content?.revenue.localTax.toLocaleString()} 원`}
                      </TextStyle>
                    </ContentContainer>
                  </IonAccordion>
                </IonAccordionGroup>
                <IonAccordionGroup>
                  <IonAccordion value={'price'}>
                    <StyleIonItemInfo slot={'header'}>
                      <TextStyle type={'r_14'} color={'primary_light'}>
                        사화, 골동품 양도소득 계산법
                      </TextStyle>
                    </StyleIonItemInfo>
                    <ContentContainer slot="content">
                      <TextStyle type={'b_12'} color={'primary_light'}>
                        {'서화, 골동품 양도소득 계산법'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 양도가액: 분할 소유권 보유 수량*분할 소유권 매각 단가'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 취득가액: 분할 소유권 보유 수량*평균 취득가'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 취득 환산가액: 양도가액*인정 필요경비 비율'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 인정 필요경비: 취득가액과 취득환산가액 중 큰 금액'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 기타소득금액: 양도가액 - 수수료 - 인정 필요경비'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 과세대상 금액 = 기타소득금액이 5만원 이상일 시 해당'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 기타 소득세 = 기타소득금액*20%(원 단위 이하 금액은 절삭)'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'- 지방 소득세 = 기타 소득세 * 10'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'(작품 매각 가격 1억 이하: 90%)'}
                      </TextStyle>
                      <TextStyle type={'r_12'} color={'primary_light'}>
                        {'(작품 매각 가격 1억 초과: (9천만원 +1억 초과분의 80%)/매각 가격 *100'}
                      </TextStyle>
                    </ContentContainer>
                  </IonAccordion>
                </IonAccordionGroup>
              </SectionContainer>
              {detailStore.content?.userProductStatus === 'TO_BE_SETTLEMENT' && (
                <>
                  <DivideLineBold />
                  <SectionContainer>
                    <TextStyle type={'b_16'}>주민등록번호 입력</TextStyle>
                    <CustomSpacer spacing="16px" />
                    <StyleIonInputNew
                      type={'text'}
                      inputmode={'text'}
                      inputType={'INPUT'}
                      inputStyle={'BOX'}
                      placeholder={'주민등록번호를 입력해주세요.'}
                      name={'rrn'}
                      onInput={onChangeInput}
                    />
                    <TextStyle type={'r_12'} color={'primary_light'}>
                      소득세 신고를 위한 주민등록번호 제공이 필수입니다.
                    </TextStyle>
                  </SectionContainer>
                  <DivideLineBold />
                  <SectionContainer>
                    <BetweenWrap>
                      <div className="content">
                        <TextStyle type={'r_14'}>
                          매각 대금을 지급받음으로써 미술작품 매각 관련 절차가 완료되었음을 확인합니다. (필수)
                        </TextStyle>
                      </div>
                      <IonCheckbox
                        className="check_box"
                        aria-label={'check_box'}
                        mode="md"
                        slot="end"
                        name="terms1"
                        checked={checkedTerms.terms1}
                        onIonChange={onCheckChange}
                      ></IonCheckbox>
                    </BetweenWrap>
                    <CustomSpacer spacing="16px" bottomLine={true} />
                    <CustomSpacer spacing="16px" />
                    <BetweenWrap>
                      <div className="content">
                        <TextStyle type={'r_14'}>소득세 신고를 위한 개인정보 제공에 동의합니다. (필수)</TextStyle>
                      </div>
                      <IonCheckbox
                        className="check_box"
                        aria-label={'check_box'}
                        mode="md"
                        slot="end"
                        name="terms2"
                        checked={checkedTerms.terms2}
                        onIonChange={onCheckChange}
                      ></IonCheckbox>
                    </BetweenWrap>
                  </SectionContainer>
                </>
              )}
            </>
          )}
        </DetailContent>
      </IonContent>
      <IonFooter>
        <IonButtonWrap>
          {detailStore.isLoading && null}
          {detailStore.isFailed && null}
          {detailStore.isLoaded && (
            <>
              {detailStore.content?.status === 'VOTING' && !detailStore.content?.voteType && (
                <FooterButtons
                  button={{
                    color: 'danger',
                    text: '매각 반대',
                    onClick: () => onDisposalVote('REJECT'),
                  }}
                  rightButton={{ color: 'dark', text: '매각 찬성', onClick: () => onDisposalVote('ACCEPT') }}
                />
              )}
              {detailStore.content?.status === 'VOTING' && detailStore.content?.voteType === 'REJECT' && (
                <FooterButtons
                  button={{
                    color: 'danger',
                    text: '매각 반대 완료',
                    disabled: true,
                  }}
                />
              )}
              {detailStore.content?.status === 'VOTING' && detailStore.content?.voteType === 'ACCEPT' && (
                <FooterButtons
                  button={{
                    color: 'primary',
                    text: '매각 찬성 완료',
                    disabled: true,
                  }}
                />
              )}
              {detailStore.content?.status === 'FINISH' && !detailStore.content?.userProductStatus && (
                <FooterButtons
                  button={{
                    color: 'dark',
                    text: '투표가 종료되었습니다.',
                  }}
                />
              )}
              {detailStore.content?.status === 'FINISH' &&
                detailStore.content?.userProductStatus === 'TO_BE_SETTLEMENT' && (
                  <FooterButtons
                    button={{
                      color: 'dark',
                      text: validateStatus !== VALIDATE_STATUS.OKAY ? '정산 정보를 입력해주세요.' : '정산하기',
                      disabled: validateStatus !== VALIDATE_STATUS.OKAY ? true : false,
                      onClick: onSettlement,
                    }}
                  />
                )}
            </>
          )}
        </IonButtonWrap>
      </IonFooter>
    </IonPage>
  )
})

const IonButtonWrap = styled.div`
  display: flex;
  justify-content: space-between;
  ion-button {
    min-height: 55px;
    width: 100%;
    margin: 0;
    ion-text {
      font-size: 16px;
      font-weight: 700;
    }
  }
`

const SectionContainer = styled.div`
  padding: 24px 20px;
`

const BetweenWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  .content {
    width: 90%;
  }
  .check_box {
    margin: 5px 8px 0 0;
    --border-width: 2px;
    --border-color-checked: #000000;
    --checkbox-background-checked: #000000;
    --checkmark-width: 3px;
  }
`

const CardVoteContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  margin: 15px 0 24px;
  border-radius: 8px;
  .vertical-line {
    border-left: 1px solid;
  }
`

const GuideVoteContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
  padding: 16px 20px;
`

const GuideTradeContainer = styled.div`
  padding: 17px 20px;
  display: flex;
  flex-direction: column;
  font-size: 1px;
  ul {
    padding-left: 20px;
    font-size: 13px;
    color: var(--ion-color-primary_light);
    line-height: 21px;
    margin: 5px 0;
  }
`
const CardContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  line-height: 18px;
  width: 50%;
`

const CardFinishContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  margin: 24px 20px 0;
  @media (prefers-color-scheme: light) {
    background-color: #000000;
  }
  @media (prefers-color-scheme: dark) {
    background-color: #4c7eff;
  }
  border-radius: 8px;
  .vertical-line {
    border-left: 1px solid var(--ion-color-white);
  }
`

const AccordionContentWrap = styled.div`
  padding: 0 20px 20px 20px;
  line-height: 33px;
`

const ContentWrap = styled.div`
  padding: 10px 0;
  line-height: 33px;
`

const StyleIonItem = styled(IonItem)`
  .ion-accordion-toggle-icon {
    color: var(--ion-color-gray300);
  }
  --inner-padding-end: 49%;
  --inner-padding-top: 2px;
  --inner-padding-bottom: 2px;
  @media (prefers-color-scheme: light) {
    --background: #ffffff;
  }
  @media (prefers-color-scheme: dark) {
    --background: #00000000;
  }
`

const StyleBox = styled.div`
  color: #ffffff;
  background-color: var(--ion-color-secondary_main);
  height: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`

const BottomContainer = styled.div`
  position: fixed;
  display: flex;
  justify-content: center;
  left: 0;
  right: 0;
  bottom: 0;
  height: 125px;
  background-color: #ffff;
  border-top: solid 1px #efefef;
  span {
    margin-top: 7px;
  }
`

const StyleIonItemPrice = styled(IonItem)`
  --padding-top: 0;
  --padding-bottom: 0;
  --padding-start: 0;
  --padding-end: 0;
  --inner-padding-end: 0;
  ion-icon {
    margin: 0;
  }
`
const StyleIonItemInfo = styled(IonItem)`
  --padding-top: 0;
  --padding-bottom: 0;
  --padding-start: 0;
  --padding-end: 0;
  --inner-padding-end: 0;
  ion-icon {
    margin: 0;
    color: var(--ion-color-primary_light);
  }
`

const ContentContainer = styled.div`
  background-color: var(--ion-color-gray100);
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`
